import './index.css';
import './lib/i18n';
import { Auth0Provider } from '@auth0/auth0-react';
import { ErrorBoundary } from '@sentry/react';
import { loadStripe } from '@stripe/stripe-js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// trying to speed up stripe loading by declaring this here so it's ready to go by the time it's needed
// Solo cargar Stripe si la clave está definida y no está vacía
export const stripePromise = import.meta.env.VITE_APP_STRIPE_KEY
  ? loadStripe(import.meta.env.VITE_APP_STRIPE_KEY)
  : Promise.resolve(null);

export const AUTH0_REDIRECT_URI =
  import.meta.env.VITE_APP_OYSTEHR_APPLICATION_REDIRECT_URL_TELEMED &&
  location.href.includes(import.meta.env.VITE_APP_OYSTEHR_APPLICATION_REDIRECT_URL_TELEMED)
    ? import.meta.env.VITE_APP_OYSTEHR_APPLICATION_REDIRECT_URL_TELEMED
    : import.meta.env.VITE_APP_OYSTEHR_APPLICATION_REDIRECT_URL;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

//const IS_LOCAL = import.meta.env.VITE_APP_IS_LOCAL === 'true';
const DOMAIN = import.meta.env.VITE_APP_OYSTEHR_APPLICATION_DOMAIN || '';

// Configuración de Auth0Provider para Oystehr (EHR - Profesionales)
const auth0Config = {
  domain: DOMAIN,
  clientId: import.meta.env.VITE_APP_OYSTEHR_APPLICATION_CLIENT_ID || '',
  authorizationParams: {
    audience: import.meta.env.VITE_APP_OYSTEHR_APPLICATION_AUDIENCE,
    redirect_uri: AUTH0_REDIRECT_URI,
    scope: 'openid profile email offline_access',
    // NO forzamos una conexión específica para usar la configuración predeterminada de Oystehr
    // Si necesitas forzar una conexión específica, descomenta una de estas líneas:
    // connection: 'sms',  // Para Passwordless SMS
    // connection: 'Username-Password-Authentication',  // Para Email + Password
    // connection: import.meta.env.VITE_APP_OYSTEHR_CONNECTION_NAME,  // Para usar variable de entorno
  },
  cacheLocation: 'localstorage' as const,
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
  // Callback para manejar la redirección después del login
  onRedirectCallback: (appState: any) => {
    // Guardar el destino en localStorage para que AuthCallbackPage pueda usarlo
    if (appState?.returnTo || appState?.target) {
      const destination = appState.returnTo || appState.target;
      localStorage.setItem('redirectDestination', destination);
    }
  },
};

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.log(String(error), errorInfo);

          // Ignorar errores de autenticación silenciosa de Auth0
          const errorString = String(error);
          if (
            errorString.includes('login_required') ||
            errorString.includes('consent_required') ||
            errorString.includes('interaction_required')
          ) {
            console.log('Auth0 silent authentication failed - this is expected for users not logged in');
            return;
          }

          // Ignorar errores de Stripe cuando no hay clave configurada
          if (errorString.includes('IntegrationError') && errorString.includes('Stripe')) {
            console.log('Stripe error - likely missing API key in .env.local');
            return;
          }

          if (errorString.includes('TypeError: Failed to fetch dynamically imported module')) {
            location.reload();
          }
        }}
        fallback={<p>An error has occurred</p>}
      >
        <Auth0Provider {...auth0Config}>
          <App />
        </Auth0Provider>
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>
);
